import React, { useState } from "react";
import { observer } from "mobx-react";
import dashboardStore from "../../../store/dashboardStore";
import commonStore from "../../../../Store/marco/commonStore";
import styles from "../../../styles/matricCard.module.css";
import CustomTreeSelect from "../../../../Tools/CustomTreeSelect";
import CompanySelector from "../../../../Tools/CompanySelector";
import { MenuItem, Select } from "@mui/material";
import { IntensityLineChart } from "./charts/IntensityLineChart";
import { parseDecimalInput } from "../../../utils/parseDecimalInput";
import { useTranslation } from "react-i18next";

const ARVersionSelect = ({ value, onChange, width = "100%" }) => {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      sx={{
        height: 40,
        width,
        borderRadius: "6px",
        background: "white",
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none !important",
        },
      }}
      MenuProps={{
        sx: { zIndex: 300 },
        PaperProps: {
          sx: {
            "& .MuiMenuItem-root": {
              fontSize: "14px",
              paddingY: 0.8,
            },

            // ★★★ hover 背景色在這裡調整 ★★★
            "& .MuiMenuItem-root:hover": {
              backgroundColor: "rgb(245,245,245)", // 你想要的 hover 顏色
            },

            "& .Mui-selected": {
              backgroundColor: "#cce7ff !important", // 選中背景色
            },
          },
        },
      }}
    >
      <MenuItem value={2}>AR5</MenuItem>
      <MenuItem value={3}>AR6</MenuItem>
    </Select>
  );
};

export const MatricCard = observer(({ card, index }) => {
  const store = dashboardStore;
  const cmStore = commonStore;
  const { t } = useTranslation("ActivityMain");
  const { t: tCommon } = useTranslation("common");
  const [companyMenuOpen, setCompanyMenuOpen] = useState(false);
  const companyOptions = cmStore.companySelectorList;
  const selectedCompanyIds = Array.isArray(
    store.metricData[index]["companyIds"],
  )
    ? store.metricData[index]["companyIds"]
    : [];

  const handleRemove = (idx) => {
    store._handleSetDeleteIndex(idx);
    store._handleSetShowDeleteWarningModal(true);
  };

  // 更新一般欄位（name, label, ARVersion, entity）
  const updateField = (key, value) => {
    const updatedCard = { ...card, [key]: value };

    store._handleSetDashboardIsEdit(true);
    store._handleUpdateMetricByField(index, key, value);
    if (key === "companyIds" || key === "gwpId") {
      store.updateSingleIntensityData(updatedCard, index);
    }
  };

  // 更新年度資料（actualByYear / denominatorByYear）
  const updateYear = (type, yearIndex, value) => {
    store._handleSetDashboardIsEdit(true);
    store._handleUpdateYearValue(index, type, yearIndex, value);
  };

  // 更新 Scope
  const toggleScope = (scopeKey) => {
    const current = card.scopes[scopeKey].default;

    const newScopes = {
      ...card.scopes,
      [scopeKey]: {
        ...card.scopes[scopeKey],
        default: !current,
      },
    };

    store._handleSetDashboardIsEdit(true);
    store._handleUpdateMetricByField(index, "scopes", newScopes);
    store.updateSingleIntensityDataByScope(
      { ...card, scopes: newScopes },
      index,
    );
  };

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <h4 className={styles.cardTitle}>{card.metricName}</h4>
        <button
          className={styles.removeBtn}
          onClick={() => {
            handleRemove(index);
          }}
        >
          {t("Remove")}
        </button>
      </div>

      {/* Name + Denominator Label */}
      <div className={styles.header}>
        <div className={styles.col}>
          <label>{t("Name")}</label>
          <input
            value={card.metricName}
            maxLength={20}
            onChange={(e) => updateField("metricName", e.target.value)}
          />
        </div>

        <div className={styles.col}>
          <label>{t("Denominator Label")}</label>
          <input
            value={card.denomLabel}
            maxLength={20}
            onChange={(e) => updateField("denomLabel", e.target.value)}
          />
        </div>
      </div>

      {/* Entity */}
      <div className={styles.dropdownWrap}>
        <div className={styles.selectedComWrap}>
          {cmStore.isCustomTree ? (
            <CustomTreeSelect
              companyList={companyOptions}
              selectedIds={selectedCompanyIds}
              onChange={(ids) => updateField("companyIds", ids)}
              sx={{
                width: "100%",
                height: "40px",
                backgroundColor: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  top: 0,
                  border: "none",
                },
                "& legend": {
                  display: "none",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  paddingY: 0,
                },
              }}
              dropdownMaxHeight={400}
              variant="dashboard"
              menuZIndex={300}
            />
          ) : (
            <CompanySelector
              companyOptions={companyOptions}
              selectedCom={selectedCompanyIds}
              setSelectedCom={(ids) => updateField("companyIds", ids)}
              menuOpen={companyMenuOpen}
              setMenuOpen={setCompanyMenuOpen}
              iconColor="#555555"
              disableOutline
              sx={{
                width: "100%",
                height: "40px",
                backgroundColor: "white",
              }}
            />
          )}
        </div>

        <div className={styles.ARSelectWrap}>
          <ARVersionSelect
            value={Number(card.gwpId)} // 確保是數字
            onChange={(val) => updateField("gwpId", val)}
          />
        </div>
      </div>

      {/* Scopes */}
      <div>
        <label className={styles.sectionTitle}>{t("Scopes")}</label>

        <div className={styles.scopeRow}>
          {["scope1", "scope2", "scope3"].map((s) => (
            <label key={s} className={styles.scopeItem}>
              <input
                type="checkbox"
                checked={card.scopes[s].default}
                disabled={card.scopes[s].disabled}
                onChange={() => toggleScope(s)}
              />
              {tCommon(`Scope ${s.slice(-1)}`)}
            </label>
          ))}
        </div>
      </div>

      {/* Actual */}
      <div className={styles.actualField}>
        <div className={styles.actualTitle}>
          {t("Actual Emissions by Year (tCO₂e)")}
        </div>

        <div className={styles.yearGrid}>
          {card.actualByYear.map((item, y) => (
            <div className={styles.yearCol} key={y}>
              <label className={styles.yearLabel}>{item.year}</label>
              <input
                className={styles.yearInput}
                disabled={card.systemYears?.includes(item.year)}
                value={item.value ?? ""}
                type="text"
                onChange={(e) => {
                  const result = parseDecimalInput(e.target.value);
                  updateYear("actualByYear", y, result);
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Denominator */}
      <div>
        <div className={styles.denominatorTitle}>
          {t("Denominator by Year")} ({t(card.denomLabel)})
        </div>

        <div className={styles.yearGrid}>
          {card.denominatorByYear.map((item, y) => (
            <div className={styles.yearCol} key={y}>
              <label className={styles.yearLabel}>{item.year}</label>
              <input
                className={styles.yearInput}
                value={item.value ?? ""}
                type="text"
                onChange={(e) => {
                  const result = parseDecimalInput(e.target.value);
                  updateYear("denominatorByYear", y, result);
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className={styles.chartArea}>
        <IntensityLineChart card={card} index={index} />
      </div>
    </div>
  );
});
